import { useState, useEffect, useCallback } from 'react';
import { useUser } from '../../auth/hooks/useUser';
import { DivisionService } from '../services/DivisionService';

export const useUserDivisions = () => {
  const { userData } = useUser();
  const [divisions, setDivisions] = useState([]);
  const [isLoading, setIsLoading] = useState(false);

  const fetchDivisions = useCallback(() => {
    if (!userData?.userId) return Promise.resolve();

    setIsLoading(true);
    return DivisionService.getUserDivisions(userData.userId)
      .then(result => setDivisions(Array.isArray(result) ? result : []))
      .catch(() => setDivisions([]))
      .finally(() => setIsLoading(false));
  }, [userData?.userId]);

  useEffect(() => {
    let cancelled = false;
    if (!userData?.userId) return;

    setIsLoading(true);
    DivisionService.getUserDivisions(userData.userId)
      .then(result => { if (!cancelled) setDivisions(Array.isArray(result) ? result : []); })
      .catch(() => { if (!cancelled) setDivisions([]); })
      .finally(() => { if (!cancelled) setIsLoading(false); });

    return () => { cancelled = true; };
  }, [userData?.userId]);

  // Membership can change from anywhere (adding/removing a division member, deleting a
  // division, bulk-updating a user's divisions) — this hook otherwise only fetches once on
  // mount with no cache, so without this listener the division switcher and any other
  // consumer stay stuck showing pre-mutation state until a full page reload.
  useEffect(() => {
    const handleChange = () => { fetchDivisions(); };
    window.addEventListener('userDivisionsChanged', handleChange);
    return () => window.removeEventListener('userDivisionsChanged', handleChange);
  }, [fetchDivisions]);

  return { divisions, isLoading, refresh: fetchDivisions };
};
